import type { APIError } from "@/types/common"
import type { AppNotification, BroadcastNotification } from "@/types/notifications"
import _orderBy from "lodash/orderBy"
import { acceptHMRUpdate, defineStore } from "pinia"
import Api from "@/api"
import { useGlobalActions } from "@/composables/common/useGlobalActions"
import { BroadcastNotificationCategory } from "@/types/notifications"
import { broadcastToAppNotification, broadcastToNotificationObject } from "@/utils/notifications"

export const useNotificationsStore = defineStore("notifications", {
	state: () => ({
		unread: false,
		broadcastNotifications: [] as BroadcastNotification[]
	}),
	actions: {
		setList(list: BroadcastNotification[], options?: { category?: BroadcastNotificationCategory }) {
			const notificationsList = options?.category
				? [...this.broadcastNotifications.filter(n => n.category !== options.category), ...list]
				: list
			this.broadcastNotifications = _orderBy(notificationsList, ["id"], ["desc"])
		},
		setUnreadState(val: boolean) {
			this.unread = val
		},

		async getList(options?: {
			notify?: boolean
			unread?: boolean
			category?: BroadcastNotificationCategory
		}): Promise<BroadcastNotification[]> {
			// EVALUATE: paginazione notifiche
			try {
				this.setUnreadState(options?.unread || false)

				const response = await Api.common.notifications.list({
					perPage: 200,
					page: 1,
					filters: { read: options?.unread ? false : undefined, category: options?.category || undefined }
				})
				const list = response.data.items || []

				if (options?.notify) {
					const toNotify = list.filter(n => n.id > (this.broadcastNotifications?.[0]?.id || 0))
					for (const n of toNotify) {
						useGlobalActions().notification(broadcastToNotificationObject(n))
					}
				}

				this.setList(list, { category: options?.category || undefined })

				return list
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			}
		},

		async setRead(id: number): Promise<boolean> {
			try {
				await Api.common.notifications.setOneRead(id, "read")

				const notification = this.broadcastNotifications.find(n => n.id === id)
				if (notification) {
					notification.read = true
				}

				return true
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			}
		},

		async deleteOne(id: number): Promise<boolean> {
			try {
				await Api.common.notifications.deleteOne(id)
				await this.getList()

				return true
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			}
		},

		async setAllRead(): Promise<boolean> {
			try {
				await Api.common.notifications.setAllRead()

				for (const n of this.broadcastNotifications) {
					n.read = true
				}

				return true
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			}
		},

		async deleteAll(): Promise<boolean> {
			try {
				await Api.common.notifications.setAllRead()
				await this.getList()

				return true
			} catch (err) {
				const error = err as APIError
				throw error.response?.data || error
			}
		}
	},
	getters: {
		count(state): number {
			return state.broadcastNotifications.length
		},
		countUnread(state): number {
			return state.broadcastNotifications.filter(n => !n.read).length
		},
		hasUnread(): boolean {
			return !!this.countUnread
		},
		hasNotifications(): boolean {
			return !!this.count
		},
		notifications(state): AppNotification[] {
			return state.broadcastNotifications.map(n => broadcastToAppNotification(n))
		},
		notificationsGeneric(state): AppNotification[] {
			return state.broadcastNotifications
				.filter(n => n.category === BroadcastNotificationCategory.GENERIC)
				.map(n => broadcastToAppNotification(n))
		},
		notificationsInvitation(state): AppNotification[] {
			return state.broadcastNotifications
				.filter(n => n.category === BroadcastNotificationCategory.INVITATION)
				.map(n => broadcastToAppNotification(n))
		},
		notificationsMessage(state): AppNotification[] {
			return state.broadcastNotifications
				.filter(n => n.category === BroadcastNotificationCategory.MESSAGE)
				.map(n => broadcastToAppNotification(n))
		},
		notificationsReport(state): AppNotification[] {
			return state.broadcastNotifications
				.filter(n => n.category === BroadcastNotificationCategory.EXAM_REPORT)
				.map(n => broadcastToAppNotification(n))
		}
	}
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useNotificationsStore, import.meta.hot))
}
